// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { useCallback, useRef, useState } from 'react';

export interface CloseWithoutCopyGuard {
  secretCopied: boolean;
  markSecretCopied: () => void;
  requestClose: (onConfirm: () => void) => void;
  confirmClose: () => void;
  cancelClose: () => void;
  reset: () => void;
  isWarningOpen: boolean;
}

export function useCloseWithoutCopyGuard(): CloseWithoutCopyGuard {
  const [secretCopied, setSecretCopied] = useState(false);
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const pendingConfirm = useRef<(() => void) | null>(null);

  const markSecretCopied = useCallback(() => {
    setSecretCopied(true);
  }, []);

  const requestClose = useCallback(
    (onConfirm: () => void) => {
      if (secretCopied) {
        onConfirm();
      } else {
        pendingConfirm.current = onConfirm;
        setIsWarningOpen(true);
      }
    },
    [secretCopied],
  );

  const confirmClose = useCallback(() => {
    pendingConfirm.current?.();
    pendingConfirm.current = null;
    setIsWarningOpen(false);
  }, []);

  const cancelClose = useCallback(() => {
    setIsWarningOpen(false);
  }, []);

  const reset = useCallback(() => {
    setSecretCopied(false);
    setIsWarningOpen(false);
    pendingConfirm.current = null;
  }, []);

  return { secretCopied, markSecretCopied, requestClose, confirmClose, cancelClose, reset, isWarningOpen };
}
